// Admin · müşteriler (A01–A06, A09, A17): Zod girdileri ve yanıt tipleri.
// Gizlilik: Admin yanıtlarında yolcu adı/telefonu yok; panel kullanıcısı telefonu maskeli, yolcu yalnız sayı.
import { z } from 'zod';
import { MODULES, PLAN_KEYS, type Module, type PlanKey, type TenantType } from '../enums';
import { PageQuery, Phone, type Paged } from './common';
import { CUSTOMER_KINDS } from './business';
import {
  SUBSCRIPTION_STATUSES,
  type DomainStatus,
  type PlanFeatures,
  type PlanLimits,
  type SubscriptionStatus,
  type UpgradeRequestInfo,
  type UsageLevel,
  type UsageMetric,
} from './account';
import type { WhiteLabelPalette } from './white-label';
import { COMPANY_STATUSES, type CompanyStatus } from './admin';

/** Admin'de yönetilen müşteri kiracıları (platformun kendisi hariç). */
export const CUSTOMER_TENANT_TYPES = ['SERVICE_COMPANY', 'INSTITUTION'] as const;
export type CustomerTenantType = (typeof CUSTOMER_TENANT_TYPES)[number];

const reason = z
  .string()
  .trim()
  .min(3, 'Gerekçe en az 3 karakter olmalı')
  .max(300, 'Gerekçe en fazla 300 karakter olabilir');
/** Kuruş cinsinden aylık tutar (özel fiyatlı plan için). */
const kurus = z.number().int().min(0, 'Tutar negatif olamaz').max(100_000_000_00, 'Tutar çok büyük');
const limit = z.number().int().min(1, 'Sınır en az 1 olmalı').max(10_000_000).nullable();

// ───────── A01 genel bakış ─────────

export const ATTENTION_KINDS = [
  'PAST_DUE',
  'OVER_LIMIT',
  'TRIAL_ENDING',
  'UPGRADE_REQUEST',
  'DOMAIN_FAILED',
  'SUSPENDED',
] as const;
export type AttentionKind = (typeof ATTENTION_KINDS)[number];
export const ATTENTION_KIND_LABELS: Record<AttentionKind, string> = {
  PAST_DUE: 'Ödeme gecikti',
  OVER_LIMIT: 'Plan sınırı',
  TRIAL_ENDING: 'Deneme bitiyor',
  UPGRADE_REQUEST: 'Yükseltme talebi',
  DOMAIN_FAILED: 'Alan adı doğrulanamadı',
  SUSPENDED: 'Askıda',
};

export interface AttentionItem {
  companyId: string;
  companyName: string;
  kind: AttentionKind;
  severity: 'crit' | 'warn' | 'info';
  /** "Deneme 3 gün sonra bitiyor", "SMS kotasının %112'si kullanıldı" */
  detail: string;
  /** ilgili ekran (/subscriptions, /companies/:id …) */
  href: string;
}

export interface MrrByPlan {
  planKey: PlanKey;
  planName: string;
  /** ücretli (etkin + ödeme gecikmiş) aboneliklerin toplamı, kuruş */
  mrr: number;
  companies: number;
}

export interface RecentSignup {
  id: string;
  name: string;
  type: TenantType;
  planName: string | null;
  status: CompanyStatus;
  createdAt: string;
}

/** GET /v1/admin/overview */
export interface AdminOverview {
  companies: {
    total: number;
    serviceCompanies: number;
    institutions: number;
    trial: number;
    suspended: number;
    /** son 30 günde eklenen */
    newLast30: number;
  };
  /** aktif filo (pasif araçlar hariç) */
  vehicles: number;
  vehicleCompanies: number;
  /** aktif yolcu sayısı (kişisel veri yok) */
  passengers: number;
  activeTrips: number;
  tripsToday: number;
  mrr: number;
  arr: number;
  mrrByPlan: MrrByPlan[];
  trials: {
    active: number;
    endingIn7Days: number;
    converted: number;
    lost: number;
    /** dönüşüm: ücretliye geçen / (geçen + kaybedilen); karar verilmiş deneme yoksa null */
    conversionPct: number | null;
  };
  attention: AttentionItem[];
  recentSignups: RecentSignup[];
  at: string;
}

// ───────── A02 firmalar ─────────

export const AdminCompanyQuery = PageQuery.extend({
  type: z.enum(CUSTOMER_TENANT_TYPES).optional(),
  status: z.enum(COMPANY_STATUSES).optional(),
  plan: z.enum(PLAN_KEYS).optional(),
});
export type AdminCompanyQuery = z.infer<typeof AdminCompanyQuery>;

export interface AdminCompanyRow {
  id: string;
  name: string;
  legalName: string | null;
  type: CustomerTenantType;
  /** kurum türü etiketi (Okul, Şirket…); servis firmasında null */
  kindLabel: string | null;
  status: CompanyStatus;
  planKey: PlanKey | null;
  planName: string | null;
  subscriptionStatus: SubscriptionStatus | null;
  /** servis firması: kendi aktif araçları; kurum: son 30 günde hizmet veren araçlar */
  vehicles: number;
  /** kurum: aktif yolcuları; servis firması: sözleşmeli kurumlarının aktif yolcuları */
  passengers: number;
  mrr: number;
  createdAt: string;
  /** üyelerin en son girişi */
  lastActivityAt: string | null;
}

export interface AdminCompanyList extends Paged<AdminCompanyRow> {
  counts: { all: number; SERVICE_COMPANY: number; INSTITUTION: number; TRIAL: number; SUSPENDED: number };
}

export const AdminCompanyCreateInput = z
  .object({
    name: z.string().trim().min(2, 'Firma adı en az 2 karakter olmalı').max(80, 'Firma adı en fazla 80 karakter'),
    legalName: z.string().trim().max(160, 'Ticari unvan en fazla 160 karakter').nullable().optional(),
    type: z.enum(CUSTOMER_TENANT_TYPES),
    institutionKind: z.enum(CUSTOMER_KINDS).nullable().optional(),
    city: z.string().trim().min(2, 'Şehir girin').max(40).default('İstanbul'),
    planKey: z.enum(PLAN_KEYS),
    /** 0 = deneme yok, abonelik hemen etkin */
    trialDays: z.number().int().min(0).max(60, 'Deneme en fazla 60 gün olabilir').default(14),
    /** özel fiyatlı plan (Enterprise) için aylık tutar, kuruş */
    mrr: kurus.nullable().optional(),
    owner: z.object({
      firstName: z.string().trim().min(1, 'Ad gerekli').max(60),
      lastName: z.string().trim().min(1, 'Soyad gerekli').max(60),
      phone: Phone,
      email: z.email('Geçerli bir e-posta girin').max(120).nullable().optional(),
    }),
  })
  .superRefine((v, ctx) => {
    if (v.type === 'INSTITUTION' && !v.institutionKind)
      ctx.addIssue({ code: 'custom', path: ['institutionKind'], message: 'Kurum türünü seçin' });
  });
export type AdminCompanyCreateInput = z.infer<typeof AdminCompanyCreateInput>;

export interface AdminCompanyCreated {
  id: string;
  name: string;
  slug: string;
  ownerPhoneMasked: string;
  invited: { sms: boolean; email: boolean };
  message: string;
}

// ───────── A03 firma detayı ─────────

export const AdminCompanyStatusInput = z.object({ reason });
export type AdminCompanyStatusInput = z.infer<typeof AdminCompanyStatusInput>;

export interface UpgradeDecision {
  planKey: PlanKey;
  planName: string;
  ticketNumber: string;
  status: 'APPROVED' | 'REJECTED';
  decidedAt: string;
  decidedByName: string | null;
  note: string | null;
}

export interface AdminSubscriptionInfo {
  planKey: PlanKey;
  planName: string;
  status: SubscriptionStatus;
  statusLabel: string;
  mrr: number;
  /** plan liste fiyatı (özel fiyatlı planda 0) */
  listPrice: number;
  customPrice: boolean;
  periodStart: string;
  periodEnd: string;
  trialEndsAt: string | null;
  /** deneme bitişine kalan gün (deneme değilse null; geçtiyse 0) */
  trialDaysLeft: number | null;
  vehicleLimitOverride: number | null;
  upgradeRequest: UpgradeRequestInfo | null;
  lastDecision: UpgradeDecision | null;
}

export interface AdminUsageItem {
  metric: UsageMetric;
  label: string;
  unit: string | null;
  quantity: number;
  limit: number | null;
  pct: number | null;
  level: UsageLevel;
  costKurus: number;
}

export interface AdminMemberRow {
  id: string;
  name: string;
  title: string | null;
  roleKey: string;
  roleName: string;
  status: 'INVITED' | 'ACTIVE' | 'DISABLED';
  phoneMasked: string;
  lastLoginAt: string | null;
}

export interface AdminModuleState {
  module: Module;
  label: string;
  /** firmanın kendi bayrağı */
  enabled: boolean;
  /** kurumda: kendi bayrağı VE en az bir sözleşmeli sağlayıcının bayrağı; servis firmasında = enabled */
  effective: boolean;
  inPlan: boolean;
  hides: string;
  updatedAt: string | null;
}

/** Tahakkuk (tahmini): SaaS fatura modeli yok; plan ücreti + dönem kullanım maliyeti. */
export interface AdminAccrual {
  period: string;
  planFee: number;
  usageCost: number;
  total: number;
  /** dönem içinde deneme sürümündeydi (plan ücreti tahakkuk etmez) */
  trial: boolean;
}

export interface AdminContractRow {
  id: string;
  number: string;
  /** firmanın bu sözleşmedeki rolü */
  side: 'PROVIDER' | 'CLIENT';
  counterpartId: string;
  counterpartName: string;
  status: string;
  startsAt: string;
  endsAt: string;
}

export interface AdminAuditRow {
  id: string;
  action: string;
  entity: string;
  /** platform personeli ya da firma kullanıcısı (ad soyad); sistem işi "Sistem" */
  actorName: string;
  platformActor: boolean;
  at: string;
  /** yalnız platform düzeyi kayıtlarda (bayrak, abonelik, firma durumu, marka) değerler; diğerlerinde alan adları */
  summary: string | null;
}

/** GET /v1/admin/companies/:id */
export interface AdminCompanyDetail {
  company: {
    id: string;
    name: string;
    legalName: string | null;
    type: CustomerTenantType;
    kindLabel: string | null;
    status: CompanyStatus;
    city: string;
    slug: string;
    email: string | null;
    createdAt: string;
    lastActivityAt: string | null;
  };
  counts: { vehicles: number; passengers: number; drivers: number; routes: number; tripsLast30: number };
  subscription: AdminSubscriptionInfo | null;
  usage: { period: string | null; items: AdminUsageItem[]; cost: number };
  members: AdminMemberRow[];
  modules: AdminModuleState[];
  /** kurum: sözleşmeli sağlayıcılar; servis firması: sözleşmeli kurumlar (bayrak etkisi notu için) */
  linkedCompanies: { id: string; name: string }[];
  accruals: AdminAccrual[];
  contracts: AdminContractRow[];
  audit: AdminAuditRow[];
}

// ───────── A04 abonelikler ─────────

export const AdminSubscriptionQuery = PageQuery.extend({
  status: z.enum(SUBSCRIPTION_STATUSES).optional(),
  plan: z.enum(PLAN_KEYS).optional(),
  /** yalnız bekleyen yükseltme talebi olanlar */
  pending: z.enum(['true', 'false']).optional(),
});
export type AdminSubscriptionQuery = z.infer<typeof AdminSubscriptionQuery>;

export interface AdminSubscriptionRow extends AdminSubscriptionInfo {
  companyId: string;
  companyName: string;
  companyType: CustomerTenantType;
  companyStatus: CompanyStatus;
}

export interface AdminSubscriptionList extends Paged<AdminSubscriptionRow> {
  totals: {
    mrr: number;
    arr: number;
    counts: Record<SubscriptionStatus, number>;
    pendingUpgrades: number;
    trialsEndingIn7Days: number;
  };
}

export const AdminPlanChangeInput = z.object({
  planKey: z.enum(PLAN_KEYS),
  /** özel fiyatlı plan için zorunlu; diğerlerinde verilmezse liste fiyatı */
  mrr: kurus.nullable().optional(),
  reason,
});
export type AdminPlanChangeInput = z.infer<typeof AdminPlanChangeInput>;

export const AdminTrialExtendInput = z.object({
  days: z.number().int().min(1, 'En az 1 gün').max(60, 'En fazla 60 gün uzatılabilir'),
  reason,
});
export type AdminTrialExtendInput = z.infer<typeof AdminTrialExtendInput>;

export const AdminSubscriptionReasonInput = z.object({ reason });
export type AdminSubscriptionReasonInput = z.infer<typeof AdminSubscriptionReasonInput>;

export const AdminUpgradeDecisionInput = z.object({
  decision: z.enum(['APPROVE', 'REJECT']),
  note: z.string().trim().max(300, 'Not en fazla 300 karakter olabilir').nullable().optional(),
  /** onayda hedef plan özel fiyatlıysa aylık tutar, kuruş */
  mrr: kurus.nullable().optional(),
});
export type AdminUpgradeDecisionInput = z.infer<typeof AdminUpgradeDecisionInput>;

/** Abonelik yazma uçlarının ortak yanıtı. */
export interface AdminSubscriptionResult {
  subscription: AdminSubscriptionInfo;
  /** plan değişikliğinde açılan modüller (kapatılan hiç yok) */
  enabledModules: Module[];
  message: string;
}

// ───────── A05 planlar ─────────

export interface AdminPlanItem {
  id: string;
  key: PlanKey;
  name: string;
  monthlyPrice: number;
  customPrice: boolean;
  limits: PlanLimits;
  modules: Module[];
  features: PlanFeatures;
  rank: number;
  /** bu plandaki firma sayısı (iptal hariç) ve ücretli MRR */
  companies: number;
  trialCompanies: number;
  mrr: number;
}

export interface AdminPlanList {
  items: AdminPlanItem[];
}

export const AdminPlanUpdateInput = z.object({
  name: z.string().trim().min(2, 'Plan adı en az 2 karakter olmalı').max(40),
  /** 0 = teklifle belirlenir (özel fiyat) */
  monthlyPrice: kurus,
  vehicleLimit: limit,
  passengerLimit: limit,
  smsQuota: limit,
  apiCallQuota: limit,
  modules: z.array(z.enum(MODULES)).max(MODULES.length),
  features: z.object({
    liveTracking: z.boolean(),
    notifications: z.boolean(),
    qr: z.boolean(),
    reports: z.enum(['basic', 'advanced']),
  }),
});
export type AdminPlanUpdateInput = z.infer<typeof AdminPlanUpdateInput>;

// ───────── A06 özellik bayrakları ─────────

export const AdminFlagMatrixQuery = z.object({
  q: z.string().trim().max(60).optional(),
  type: z.enum(CUSTOMER_TENANT_TYPES).optional(),
});
export type AdminFlagMatrixQuery = z.infer<typeof AdminFlagMatrixQuery>;

export interface AdminFlagRow {
  companyId: string;
  name: string;
  type: CustomerTenantType;
  status: CompanyStatus;
  planName: string | null;
  flags: Record<Module, boolean>;
  /** kurumda etkin durum (kendi VE sağlayıcı); servis firmasında null */
  effective: Record<Module, boolean> | null;
  inPlan: Module[];
  /** kurum: sözleşmeli sağlayıcılar; servis firması: sözleşmeli kurumlar */
  linked: string[];
}

export interface AdminFlagChange {
  id: string;
  companyId: string | null;
  companyName: string;
  module: Module;
  before: boolean | null;
  after: boolean;
  actorName: string;
  at: string;
}

/** GET /v1/admin/feature-flags */
export interface AdminFlagMatrix {
  items: AdminFlagRow[];
  history: AdminFlagChange[];
}

// ───────── A09 kullanım ve maliyet ─────────

export const AdminCostQuery = z.object({
  period: z
    .string()
    .regex(/^\d{4}-\d{2}$/, 'Dönem YYYY-AA biçiminde olmalı')
    .optional(),
});
export type AdminCostQuery = z.infer<typeof AdminCostQuery>;

/** Maliyeti olan ölçümler (araç ve yolcu sayısı kullanım ölçüsüdür, sağlayıcı maliyeti yok). */
export const COST_METRICS = [
  'MAP_LOADS',
  'DIRECTIONS',
  'GEOCODING',
  'SMS',
  'AI_RUNS',
  'API_CALLS',
  'STORAGE_MB',
] as const satisfies readonly UsageMetric[];
export type CostMetric = (typeof COST_METRICS)[number];

/**
 * Birim maliyet varsayımları (kuruş/birim): sağlayıcı sözleşmesi yok; adaptör varsayımları. Ölçüm satırlarındaki
 * `costKurus` bu varsayımlarla üretilir; gerçek sağlayıcı fiyatı gelince yalnız bu tablo ve adaptör değişir.
 */
export const COST_UNIT_ASSUMPTIONS: Record<CostMetric, { kurus: number; per: string; source: string }> = {
  MAP_LOADS: {
    kurus: 0.4,
    per: 'yükleme',
    source: 'Harita karo sağlayıcısı (MapLibre geliştirme · Google üretim kararı açık)',
  },
  DIRECTIONS: { kurus: 1.5, per: 'istek', source: 'Rota hesaplama (OSRM geliştirme · ücretli sağlayıcı varsayımı)' },
  GEOCODING: { kurus: 1.2, per: 'istek', source: 'Adres arama sağlayıcısı varsayımı' },
  SMS: { kurus: 18, per: 'mesaj', source: 'Türk SMS sağlayıcısı varsayımı (geliştirmede mock)' },
  AI_RUNS: { kurus: 2500, per: 'çalıştırma', source: 'Optimizasyon işlem maliyeti varsayımı' },
  API_CALLS: { kurus: 0.02, per: 'çağrı', source: 'API ağ geçidi ve işlem maliyeti varsayımı' },
  STORAGE_MB: { kurus: 0.5, per: 'MB/ay', source: 'Nesne depolama varsayımı' },
};

export interface AdminCostMetricTotal {
  metric: CostMetric;
  label: string;
  unit: string | null;
  quantity: number;
  cost: number;
}

export interface AdminCostCompanyRow {
  companyId: string;
  name: string;
  type: CustomerTenantType;
  planName: string | null;
  subscriptionStatus: SubscriptionStatus | null;
  /** tanınan gelir: ücretli abonelik MRR'ı; denemede 0 */
  revenue: number;
  cost: number;
  margin: number;
  marginPct: number | null;
  metrics: Partial<Record<CostMetric, { quantity: number; cost: number }>>;
}

export interface AdminCostTrendPoint {
  period: string;
  cost: number;
  /** ölçüm bazında maliyet, kuruş */
  byMetric: Record<CostMetric, number>;
}

/** GET /v1/admin/costs */
export interface AdminCostReport {
  period: string;
  periods: string[];
  totals: { cost: number; revenue: number; margin: number; marginPct: number | null; negativeCompanies: number };
  byMetric: AdminCostMetricTotal[];
  /** tüm dönemler (eskiden yeniye) */
  trend: AdminCostTrendPoint[];
  companies: AdminCostCompanyRow[];
}

// ───────── A17 beyaz etiket ─────────

export interface AdminWhiteLabelRow {
  companyId: string;
  companyName: string;
  type: CustomerTenantType;
  flagEnabled: boolean;
  configured: boolean;
  appName: string | null;
  logoUrl: string | null;
  palette: WhiteLabelPalette | null;
  /** marka rengi üzerinde koyu yazı oranı */
  contrast: number | null;
  contrastError: string | null;
  customDomain: string | null;
  domainStatus: DomainStatus;
  domainStatusLabel: string;
  /** DNS kayıtları (müşteriye iletilecek) */
  dns: { cname: { name: string; value: string }; txt: { name: string; value: string } } | null;
  updatedAt: string | null;
}

/** GET /v1/admin/white-label */
export interface AdminWhiteLabelList {
  items: AdminWhiteLabelRow[];
  cnameTarget: string;
}

export const AdminDomainStatusInput = z.object({
  status: z.enum(['VERIFIED', 'FAILED']),
  note: z.string().trim().max(300).nullable().optional(),
});
export type AdminDomainStatusInput = z.infer<typeof AdminDomainStatusInput>;
